import { useCallback } from "react";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Colors, Icon, TooltipWrapper } from "@zapier/design-system";
import {
  selectedNodeTypeAtom,
  cursorAtom,
  CursorStates,
} from "@zapier/canvas-state";

import { PathNodeIcon } from "../icons/PathNodeIcon";
import { GroupIcon } from "../icons/GroupIcon";

import styles from "./DiagrammingToolbar.module.css";
import { DefaultNodeIcon } from "./DefaultNodeIcon";
import { useSelectNodeType } from "./useSelectNodeType";

type Props = {
  isDisabled: boolean;
};

export function BasicNodeOptions({ isDisabled }: Props) {
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const { maybeSelectNodeType } = useSelectNodeType();

  const maybeSetCursorMode = useAtomCallback(
    useCallback((get, set, selectedCursorMode: CursorStates) => {
      const cursorMode = get(cursorAtom);
      if (selectedCursorMode === cursorMode) {
        set(cursorAtom, "select");
      } else {
        set(cursorAtom, selectedCursorMode);
      }
    }, []),
  );

  const listItemClass = isDisabled
    ? `${styles.dndnode} ${styles.dndnodeDisabled}`
    : styles.dndnode;

  return (
    <ul className={styles.horizontalList}>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "default",
        })}
      >
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Default step"}
          tooltipId="default-step"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("default");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("default");
              }}
            >
              <DefaultNodeIcon
                color={
                  selectedNodeType === "default"
                    ? Colors.UiPrimaryStrongest
                    : Colors.GrayWarm10
                }
              />
            </button>
          )}
        </TooltipWrapper>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "path",
        })}
      >
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Split path"}
          tooltipId="split-path"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("path");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("path");
              }}
            >
              <PathNodeIcon />
            </button>
          )}
        </TooltipWrapper>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "text",
        })}
      >
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Text"}
          tooltipId="text"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("text");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("text");
              }}
            >
              <Icon
                name="formText"
                color={
                  selectedNodeType === "text"
                    ? "UiPrimaryStrongest"
                    : "GrayWarm10"
                }
                size={30}
              />
            </button>
          )}
        </TooltipWrapper>
      </li>
      <li
        className={classNames(listItemClass, {
          [styles.dndnodeSelected]: selectedNodeType === "group",
        })}
      >
        <TooltipWrapper
          position="north"
          content={isDisabled ? "" : "Group"}
          tooltipId="group"
        >
          {({ childProps }) => (
            <button
              aria-describedby={childProps["aria-describedby"]}
              className={styles.dndButton}
              disabled={isDisabled}
              onClick={() => {
                maybeSelectNodeType("group");
                maybeSetCursorMode("add-group");
              }}
              onKeyDown={() => {
                maybeSelectNodeType("group");
                maybeSetCursorMode("add-group");
              }}
            >
              <GroupIcon isSelected={selectedNodeType === "group"} />
            </button>
          )}
        </TooltipWrapper>
      </li>
    </ul>
  );
}
